import styled from '@emotion/styled'
import { Box, Typography } from '@mui/material'
import React, { useEffect } from 'react'

import { LoadWhenInView } from '@js/common/components/LoadWhenInView'
import { getUrlByDomain } from '@js/common/getUrlByDomain'

import { CTAButtonWithWT } from '@f-fertighaus/applications/Next/components/common/CTAButton'
import ErrorBoundary from '@f-fertighaus/applications/Next/components/common/ErrorBoundary'
import { HouseCarouselSkeletonLoader } from '@f-fertighaus/applications/Next/components/common/HouseCarouselSkeletonLoader'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { SimilarHousesCarousel } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/SimilarHouses/SimilarHousesCarousel'
import {
  SimilarHousesSectionEmpty,
  SimilarHousesSectionLoading
} from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/SimilarHouses/SimilarHousesSectionPlaceholder'

import { useSimilarHousesData } from './hooks/useSimilarHousesData'
import { useSimilarHousesSearchParam } from './hooks/useSimilarHousesSearchParam'

export const SimilarHouses = () => {
  const { state: houseDetailData, domain } = useHouseDetail()
  const { data: similarHouses, status } = useSimilarHousesData(
    houseDetailData.nid
  )
  const getUrl = getUrlByDomain(domain)

  useEffect(() => {
    webtrack({
      category: 'item-collection',
      action: 'display',
      label: 'similar-houses',
      location: 'similar-houses-section',
      values: [{ type: 'house_nid', value: houseDetailData.nid }]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const similarHousesSearchParam = useSimilarHousesSearchParam(houseDetailData)

  if (
    (!similarHouses || similarHouses.length === 0) &&
    (status === 'success' ||
      status === 'error' ||
      status === 'houseNid-missing')
  ) {
    return <SimilarHousesSectionEmpty />
  }

  if (status === 'idle' || status === 'loading') {
    return <SimilarHousesSectionLoading />
  }

  return (
    <SimilarHousesContainer>
      <Box sx={{ pb: 2 }}>
        <Typography component='h2'>
          <Typography variant='mediumTitle' component='span' display='block'>
            {t('houseDetailPage.similar-houses.title', {
              houseName: houseDetailData.name
            })}
          </Typography>
          <Typography component='span' variant='body2' display='block'>
            {t('houseDetailPage.similar-houses.for', {
              name: houseDetailData.name
            })}
          </Typography>
        </Typography>
      </Box>
      <ErrorBoundary>
        <LoadWhenInView Placeholder={() => <HouseCarouselSkeletonLoader />}>
          <SimilarHousesCarousel
            similarHouses={similarHouses}
            status={status}
          />
        </LoadWhenInView>
      </ErrorBoundary>
      <CTAContainer>
        <CTAButtonWithWT
          variant='contained'
          color='primary'
          href={`${getUrl('houseList')()}?${similarHousesSearchParam}`}
          wtPayload={{
            category: 'CTA-link',
            action: 'click',
            label: 'view-other-houses',
            indicates: ['funnel-entry'],
            location: 'similar-houses-section',
            values: [{ value: '(ICON)', type: 'text' }]
          }}>
          Weitere Häuser anzeigen
        </CTAButtonWithWT>
      </CTAContainer>
    </SimilarHousesContainer>
  )
}

export const CTAContainer = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing(2)};
  justify-content: center;
  align-items: center;
  label: CTAContainer;
`

export const SimilarHousesContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  label: SimilarHousesContainer;
`
